/**
 * console.error / console.warn için Sentry adaptörü.
 *
 * NEDEN VAR: uygulama mağazada çalışıyor ve hatalar kullanıcının cihaz
 * günlüğüne yazılıyordu — kimsenin okuyamadığı bir yere. Sentry zaten kurulu
 * ve `captureError(hata, bağlam)` imzasını sunuyor, ama console'un değişken
 * argümanlı imzasıyla uyuşmuyordu.
 *
 * Bu modül aradaki çeviriyi yapar; böylece 194 çağrı yerinde argüman sırası
 * düşünmeden **1:1 ad değişimi** yapılabilir:
 *     console.error('[FCM] token:', err)  ->  logError('[FCM] token:', err)
 *
 * Kayıp yok: metinler `etiket`e, fazladan nesneler `ek1`, `ek2`... olarak
 * bağlama yazılır ve Sentry'de olayın yanında görünür.
 */
import { captureError, captureWarning } from '@/lib/sentry'

type Baglam = Record<string, unknown>

function ayristir(args: unknown[]) {
  const metinler = args.filter((a): a is string => typeof a === 'string')
  const nesneler = args.filter((a) => typeof a !== 'string')
  const etiket = metinler.join(' ')
  // Error varsa asıl hata odur; yoksa ilk nesne; hiç nesne yoksa metnin kendisi.
  // EŞDEĞER MUTASYON: `??` yerine `||` davranışı DEĞİŞTİRMEZ. `find`
  // yalnız iki şey döner: bir `Error` örneği ya da `undefined`. Error
  // nesnesi her zaman doğru-değerlidir, ayrım yaratacak yalancı değer yok.
  const hataAdayi = nesneler.find((a) => a instanceof Error) ?? nesneler[0]
  const kalanlar = nesneler.filter((a) => a !== hataAdayi)
  return { etiket, hataAdayi, kalanlar, nesneVarMi: nesneler.length > 0 }
}

function baglamKur(etiket: string, kalanlar: unknown[]): Baglam | undefined {
  const b: Baglam = {}
  if (etiket) b.etiket = etiket
  kalanlar.forEach((k, i) => {
    b[`ek${i + 1}`] = k
  })
  return Object.keys(b).length > 0 ? b : undefined
}

/** console.error yerine. Hatayı Sentry'ye gönderir. */
export function logError(...args: unknown[]): void {
  const { etiket, hataAdayi, kalanlar, nesneVarMi } = ayristir(args)
  if (!nesneVarMi) {
    // Yalnız metin verilmiş: metnin kendisi hatadır, ayrıca etiketlenmez.
    captureError(etiket, undefined)
    return
  }
  captureError(hataAdayi, baglamKur(etiket, kalanlar))
}

/** console.warn yerine. Uyarıyı Sentry'ye gönderir. */
export function logWarn(...args: unknown[]): void {
  const { etiket, hataAdayi, kalanlar, nesneVarMi } = ayristir(args)
  if (!nesneVarMi) {
    captureWarning(etiket, undefined)
    return
  }
  // Metin yoksa başlığı Error mesajından, o da yoksa sabit bir sözcükten al.
  const mesaj = etiket || (hataAdayi instanceof Error ? hataAdayi.message : 'Uyarı')
  const kalanUyari = hataAdayi instanceof Error && !etiket ? kalanlar : [hataAdayi, ...kalanlar]
  captureWarning(mesaj, baglamKur('', kalanUyari))
}
